import { useCallback, useEffect, useMemo, useState } from 'react'

import { useFetchCompanies } from '@modules-deprecated/app/companies/hooks/useFetchCompanies'
import { Company, FrenchCompany } from '@modules-deprecated/app/companies/types'
import { getCompanyName } from '@modules-deprecated/app/companies/utils/getCompanyName'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { countriesWithCompanySearchEnabled } from '../constants/countriesWithCompanySearchEnabled'
import { CompanySearchFieldMode } from '../enums/companySearchFieldMode'
import { isDetailedCompany } from '../utils/isDetailedCompany'

type UseCompanySearchProps = {
  onChange: (name: string) => void
  onSelectCompany: (company: Partial<Company> | Partial<FrenchCompany>) => void
}

export const useCompanySearchField = ({ onChange, onSelectCompany }: UseCompanySearchProps) => {
  const [mode, setMode] = useState<CompanySearchFieldMode>(CompanySearchFieldMode.Autocomplete)
  const [selectedCompany, setSelectedCompany] = useState<Partial<Company> | Partial<FrenchCompany>>({ id: '' })
  const { organization } = useUserOrganization()
  // NL company search returns partial data (zipcode is missing). When a partial company
  // is selected, we fetch full details by id — the effect below re-calls selectCompany
  // with the complete data once the fetch resolves.
  const { companies, isFetching: isLoadingDetailedCompany } = useFetchCompanies(
    isDetailedCompany(selectedCompany) ? undefined : selectedCompany.id,
  )

  const isCompanySearchSupportedInCountry = useMemo(
    () => countriesWithCompanySearchEnabled.includes(organization?.countryId ?? ''),
    [organization?.countryId],
  )

  const isCompanySearchActive = useMemo(() => {
    return isCompanySearchSupportedInCountry && mode === CompanySearchFieldMode.Autocomplete
  }, [isCompanySearchSupportedInCountry, mode])

  const isPartialCompanySelected = useMemo(
    () => !!selectedCompany.id && !isDetailedCompany(selectedCompany),
    [selectedCompany],
  )

  const createFreeTextEntry = useCallback(
    (name: string) => {
      if (selectedCompany.id) {
        setSelectedCompany({ id: '' })
      }
      if (name.length > 0) {
        onChange(name)
        setMode(CompanySearchFieldMode.FreeText)
      }
    },
    [onChange, selectedCompany],
  )

  const selectCompany = useCallback(
    (companyId?: string, company?: Partial<Company> | Partial<FrenchCompany>) => {
      if (companyId && company) {
        setSelectedCompany(company)
        onChange(getCompanyName(company) ?? '')
        onSelectCompany(company)
      }
    },
    [onChange, onSelectCompany, setSelectedCompany],
  )

  const updateCompanyName = useCallback(
    (value: string) => {
      if (mode === CompanySearchFieldMode.FreeText) {
        onChange(value)
        if (!value) {
          setMode(CompanySearchFieldMode.Autocomplete)
        }
      }

      // When the user selects a company then manually clears the input, we reset the selection so they can search again.
      // { id: '' } signals "no company selected" to CompanySelect in a way it can act on — an empty object {} would
      // be ignored by its internal sync logic. This pairs with selectedId={selectedCompany?.id || undefined} in the
      // component, which prevents CompanySelect from calling onCreate with a stale value on blur.
      if (!value && mode === CompanySearchFieldMode.Autocomplete) {
        onChange(value)
        setSelectedCompany({ id: '' })
      }
    },
    [mode, onChange],
  )

  useEffect(() => {
    if (!isLoadingDetailedCompany && isPartialCompanySelected && companies.length > 0) {
      const detailedCompany = companies[0]
      if (detailedCompany && detailedCompany.id === selectedCompany.id) {
        selectCompany(detailedCompany.id, detailedCompany)
      }
    }
  }, [companies, isLoadingDetailedCompany, isPartialCompanySelected, selectedCompany.id, selectCompany])

  useEffect(() => {
    if (!isCompanySearchSupportedInCountry && mode === CompanySearchFieldMode.Autocomplete) {
      setMode(CompanySearchFieldMode.FreeText)
    }
  }, [isCompanySearchSupportedInCountry, mode])

  return {
    createFreeTextEntry,
    isCompanySearchActive,
    isLoadingDetailedCompany,
    selectCompany,
    selectedCompany,
    updateCompanyName,
  }
}
